// RPTEXPORT1-F2 (docs/build-briefs/RPTEXPORT1-pdf-and-google-sheets-export.md; owner ruling 2026-10-20: "Don't store in cloud.
// The user will sign in to their Google account locally on their machine when they want to export to sheets."). The browser
// half of Export to Google Sheets for the report results.
//
// At export time the Google Identity Services token client signs the person in (scope drive.file only) and hands back a
// short-lived access token. That token lives in ONE local variable of createReportSheet's caller for that one export: it is
// never written to web storage or a cookie, never put in a URL or a log, and never sent to
// our own server. The server answered the report rows (after its own permission and audit checks); the Sheets API is called
// from here, with the person's own token, and the sheet lands in the person's own Drive.
//
// The token request must start inside the click that asked for it, or the browser blocks Google's sign-in window, so
// requestExportToken() starts its request before its first await when Google's script is already loaded.

import {
  GOOGLE_DRIVE_FILE_SCOPE,
  GOOGLE_MESSAGES,
  readTokenResponse,
  tokenFailureReason,
  type TokenResponse,
} from "./googleSheetsModel.ts";
import { preloadGoogleIdentity } from "./googleSheets.ts";
import {
  SHEETS_API_URL,
  SHEETS_MESSAGES,
  buildBatchGroups,
  buildCreateBody,
  parseCreatedSpreadsheet,
  type SheetsPayload,
} from "./googleSheetsReportModel.ts";

interface TokenClient {
  requestAccessToken: (overrides?: { prompt?: string }) => void;
}
interface GoogleIdentity {
  accounts: {
    oauth2: {
      initTokenClient: (config: {
        client_id: string;
        scope: string;
        callback: (response: TokenResponse) => void;
        error_callback: (error: { type?: string }) => void;
      }) => TokenClient;
    };
  };
}

export type TokenOutcome = { ok: true; accessToken: string } | { ok: false; message: string };
export type SheetOutcome = { ok: true; url: string } | { ok: false; message: string };

function identity(): GoogleIdentity | undefined {
  return (window as unknown as { google?: GoogleIdentity }).google;
}

/** Signs the person in to Google and resolves with a fresh access token for THIS export. A new token client is made for
 * each call and nothing is cached, so no token outlives the export that asked for it. */
export function requestExportToken(clientId: string): Promise<TokenOutcome> {
  return new Promise<TokenOutcome>((resolve) => {
    const start = (): void => {
      const google = identity();
      if (google === undefined) {
        resolve({ ok: false, message: GOOGLE_MESSAGES.unavailable });
        return;
      }
      const client = google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: GOOGLE_DRIVE_FILE_SCOPE,
        callback: (response) => {
          const held = readTokenResponse(response, Date.now());
          resolve(
            held === null
              ? { ok: false, message: GOOGLE_MESSAGES.notFinished }
              : { ok: true, accessToken: held.value },
          );
        },
        error_callback: (error) => {
          resolve({
            ok: false,
            message:
              tokenFailureReason(error.type) === "blocked" ? GOOGLE_MESSAGES.blocked : GOOGLE_MESSAGES.notFinished,
          });
        },
      });
      client.requestAccessToken({ prompt: "" });
    };
    if (identity() !== undefined) {
      // Still inside the click that asked: the browser allows Google's window.
      start();
    } else {
      void preloadGoogleIdentity().then((loaded) => {
        if (loaded) start();
        else resolve({ ok: false, message: GOOGLE_MESSAGES.unavailable });
      });
    }
  });
}

async function sheetsCall(accessToken: string, url: string, body: unknown): Promise<unknown | null> {
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: { authorization: `Bearer ${accessToken}`, "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    return null;
  }
  if (response.status !== 200) return null;
  return response.json().catch(() => null);
}

/** Creates the spreadsheet in the person's own Drive and fills it in. The access token is used only in the Authorization
 * header of these calls. Nothing is retried: a failure after the spreadsheet was created says so. */
export async function createReportSheet(accessToken: string, payload: SheetsPayload): Promise<SheetOutcome> {
  const createBody = buildCreateBody(payload);
  const expectedTabs = payload.tabs.length + 1;
  const created = parseCreatedSpreadsheet(await sheetsCall(accessToken, SHEETS_API_URL, createBody), expectedTabs);
  if (created === null) return { ok: false, message: SHEETS_MESSAGES.createFailed };
  for (const requests of buildBatchGroups(payload, created.sheetIds)) {
    const done = await sheetsCall(
      accessToken,
      `${SHEETS_API_URL}/${encodeURIComponent(created.spreadsheetId)}:batchUpdate`,
      { requests },
    );
    if (done === null) return { ok: false, message: SHEETS_MESSAGES.writeFailed };
  }
  return { ok: true, url: created.url };
}
